const ALLOWED_DOMAINS = ['jusbrasil.com.br', 'juridicocerto.com']
const DEV_HOST = /^(?:dev|local)\.jusbrasil\.com\.br(?::\d+)?$/

export type CollapseReason = 'empty' | 'malformed' | 'http_scheme' | 'disallowed_host' | 'ok'

// Single source of truth for redirect accept/reject; isValidRedirectUrl delegates.
export function classifyRedirectUrl(url: string | null | undefined): {
  valid: boolean
  reason: CollapseReason
} {
  if (!url) return { valid: false, reason: 'empty' }

  // Relative paths — always safe (can't escape current origin)
  if (url.startsWith('/') && !url.startsWith('//')) return { valid: true, reason: 'ok' }

  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return { valid: false, reason: 'malformed' }
  }

  const { protocol, host } = parsed

  // Dev/local: accept http or https
  if (DEV_HOST.test(host)) {
    return /^https?:$/.test(protocol)
      ? { valid: true, reason: 'ok' }
      : { valid: false, reason: 'disallowed_host' }
  }

  // Production: allowed domains + subdomains, HTTPS only
  const hostAllowed = ALLOWED_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`))
  if (!hostAllowed) return { valid: false, reason: 'disallowed_host' }
  if (protocol !== 'https:') return { valid: false, reason: 'http_scheme' }
  return { valid: true, reason: 'ok' }
}

export function isValidRedirectUrl(url: string): boolean {
  return classifyRedirectUrl(url).valid
}

export function getSafeRedirectUrl(raw: string | null | undefined): string {
  if (!raw || !isValidRedirectUrl(raw)) return '/'
  return raw
}

// Strict same-origin check used by auth-sensitive pages (alterar-email, alterar-senha)
// where we only want to allow redirects back to the exact origin the user is currently on.
// Accepts relative paths (/configuracoes) and absolute same-origin URLs; rejects external
// domains, protocol-relative URLs (//evil.com) and opaque-origin schemes (javascript:, data:).
export function isSafeRedirect(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.origin === window.location.origin
  } catch {
    return false
  }
}
